// Self-contained, dependency-free ZIP writer with legacy ZipCrypto encryption.
//
// Produces a single-entry ZIP archive whose one "stored" (uncompressed) file is
// encrypted with the classic "Traditional PKWARE Encryption" (ZipCrypto) stream
// cipher (PKWARE APPNOTE 6.1.5). Unlike WinZip-AES, ZipCrypto archives can be
// opened natively by Windows Explorer's built-in "Extract All" with a password.
//
// Security note: ZipCrypto is cryptographically weak (known-plaintext attacks).
// It is used here purely for native Windows compatibility. For highly sensitive
// backups, prefer AES via 7-Zip.
//
// Entry data layout (stored, no deflate):
//   [12-byte encrypted header][encrypted file bytes]
// The 12-byte header's last byte equals (crc32 >>> 24) & 0xff (password check).
//
// Uses only the standard IEEE CRC-32 (0xEDB88320) table and crypto.getRandomValues.

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  (globalThis as any).crypto.getRandomValues(out);
  return out;
}

// --- CRC-32 (IEEE, 0xEDB88320) ----------------------------------------------

const CRC_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

// Single-byte CRC-32 update: (crc >>> 8) ^ TABLE[(crc ^ b) & 0xff].
function crc32Update(crc: number, b: number): number {
  return ((crc >>> 8) ^ CRC_TABLE[(crc ^ b) & 0xff]) >>> 0;
}

// Full standard CRC-32 (init 0xFFFFFFFF, final XOR 0xFFFFFFFF).
export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = crc32Update(crc, data[i]);
  return (crc ^ 0xffffffff) >>> 0;
}

// --- ZipCrypto keystream ----------------------------------------------------

class ZipCryptoKeys {
  private key0 = 0x12345678;
  private key1 = 0x23456789;
  private key2 = 0x34567890;

  constructor(password: string) {
    const pw = new TextEncoder().encode(password);
    for (let i = 0; i < pw.length; i++) this.updateKeys(pw[i]);
  }

  updateKeys(byte: number): void {
    this.key0 = crc32Update(this.key0, byte);
    // key1 = (key1 + (key0 & 0xff)) * 134775813 + 1  (mod 2^32).
    // Use Math.imul for a correct unsigned 32-bit multiply (avoids 2^53 loss).
    this.key1 = (Math.imul((this.key1 + (this.key0 & 0xff)) >>> 0, 134775813) + 1) >>> 0;
    this.key2 = crc32Update(this.key2, (this.key1 >>> 24) & 0xff);
  }

  decryptByte(): number {
    const temp = (this.key2 | 2) & 0xffff;
    return ((temp * (temp ^ 1)) >>> 8) & 0xff;
  }
}

// Encrypt plaintext with ZipCrypto given already-initialized keys.
// Stream byte computed BEFORE update_keys; update_keys fed the PLAINTEXT byte.
function zipCryptoEncrypt(keys: ZipCryptoKeys, plain: Uint8Array): Uint8Array {
  const out = new Uint8Array(plain.length);
  for (let i = 0; i < plain.length; i++) {
    const p = plain[i];
    out[i] = (p ^ keys.decryptByte()) & 0xff;
    keys.updateKeys(p);
  }
  return out;
}

// Build the ZipCrypto encrypted entry data: [12-byte enc header][enc content].
export function encryptEntryZipCrypto(content: Uint8Array, password: string, crc: number): Uint8Array {
  const keys = new ZipCryptoKeys(password);

  // 12-byte header: first 11 random, last = high byte of CRC-32 (pw-check).
  const header = new Uint8Array(12);
  header.set(randomBytes(11), 0);
  header[11] = (crc >>> 24) & 0xff;

  const encHeader = zipCryptoEncrypt(keys, header);
  const encContent = zipCryptoEncrypt(keys, content);

  const out = new Uint8Array(encHeader.length + encContent.length);
  out.set(encHeader, 0);
  out.set(encContent, encHeader.length);
  return out;
}

// --- small binary writer -----------------------------------------------------

class ByteWriter {
  private chunks: Uint8Array[] = [];
  private len = 0;
  u16(v: number): void { const b = new Uint8Array(2); b[0] = v & 0xff; b[1] = (v >>> 8) & 0xff; this.push(b); }
  u32(v: number): void {
    const b = new Uint8Array(4);
    b[0] = v & 0xff; b[1] = (v >>> 8) & 0xff; b[2] = (v >>> 16) & 0xff; b[3] = (v >>> 24) & 0xff;
    this.push(b);
  }
  bytes(b: Uint8Array): void { this.push(b); }
  private push(b: Uint8Array): void { this.chunks.push(b); this.len += b.length; }
  get length(): number { return this.len; }
  concat(): Uint8Array {
    const out = new Uint8Array(this.len);
    let p = 0;
    for (const c of this.chunks) { out.set(c, p); p += c.length; }
    return out;
  }
}

// DOS date/time from a JS Date (local time). Returns { time, date } as u16s.
function dosDateTime(d: Date): { time: number; date: number } {
  const time = ((d.getHours() & 0x1f) << 11) | ((d.getMinutes() & 0x3f) << 5) | ((Math.floor(d.getSeconds() / 2)) & 0x1f);
  const year = Math.max(1980, d.getFullYear());
  const date = (((year - 1980) & 0x7f) << 9) | (((d.getMonth() + 1) & 0x0f) << 5) | (d.getDate() & 0x1f);
  return { time, date };
}

// One ZIP entry to encrypt+store.
export interface ZipEntry { name: string; bytes: Uint8Array; }

const GP_FLAG = 0x0001;   // bit 0: encrypted
const METHOD_STORED = 0;  // stored (no compression)

// Build a valid MULTI-entry ZIP archive: each entry is "stored" (no deflate)
// and encrypted with legacy ZipCrypto under `password`. Layout is:
//   [local header + entry data] * N  |  [central dir record] * N  |  EOCD
// Each central-directory record's relative-offset field points at that entry's
// local file header (running offset from the start of the archive). The EOCD
// records the true entry count, central-directory size, and its start offset.
export async function buildEncryptedZipMulti(
  entries: ZipEntry[],
  password: string,
): Promise<Uint8Array> {
  const { time, date } = dosDateTime(new Date());

  const localChunks: Uint8Array[] = [];
  const centralChunks: Uint8Array[] = [];
  let runningOffset = 0; // offset of the NEXT local header from archive start

  for (const e of entries) {
    const nameBytes = new TextEncoder().encode(e.name);
    const crc = crc32(e.bytes);
    const entryData = encryptEntryZipCrypto(e.bytes, password, crc);
    const compressedSize = entryData.length;   // 12 + content.length
    const uncompressedSize = e.bytes.length;
    const localHeaderOffset = runningOffset;

    // Local file header (no extra field).
    const local = new ByteWriter();
    local.u32(0x04034b50);
    local.u16(20);                        // version needed to extract
    local.u16(GP_FLAG);
    local.u16(METHOD_STORED);
    local.u16(time);
    local.u16(date);
    local.u32(crc);
    local.u32(compressedSize);
    local.u32(uncompressedSize);
    local.u16(nameBytes.length);
    local.u16(0);                         // extra field length: none
    local.bytes(nameBytes);
    local.bytes(entryData);
    const localBytes = local.concat();
    localChunks.push(localBytes);
    runningOffset += localBytes.length;

    // Central directory record (no extra field), pointing at this local header.
    const central = new ByteWriter();
    central.u32(0x02014b50);
    central.u16(20);                      // version made by
    central.u16(20);                      // version needed to extract
    central.u16(GP_FLAG);
    central.u16(METHOD_STORED);
    central.u16(time);
    central.u16(date);
    central.u32(crc);
    central.u32(compressedSize);
    central.u32(uncompressedSize);
    central.u16(nameBytes.length);
    central.u16(0);                       // extra field length: none
    central.u16(0);                       // file comment length
    central.u16(0);                       // disk number start
    central.u16(0);                       // internal file attributes
    central.u32(0);                       // external file attributes
    central.u32(localHeaderOffset);
    central.bytes(nameBytes);
    centralChunks.push(central.concat());
  }

  const localTotal = runningOffset;
  let centralSize = 0;
  for (const c of centralChunks) centralSize += c.length;
  const centralOffset = localTotal;

  // End of central directory record.
  const eocd = new ByteWriter();
  eocd.u32(0x06054b50);
  eocd.u16(0);                            // disk number
  eocd.u16(0);                            // disk with central dir start
  eocd.u16(entries.length);               // entries on this disk
  eocd.u16(entries.length);               // total entries
  eocd.u32(centralSize);
  eocd.u32(centralOffset);
  eocd.u16(0);                            // comment length
  const eocdBytes = eocd.concat();

  const total = localTotal + centralSize + eocdBytes.length;
  const out = new Uint8Array(total);
  let p = 0;
  for (const c of localChunks) { out.set(c, p); p += c.length; }
  for (const c of centralChunks) { out.set(c, p); p += c.length; }
  out.set(eocdBytes, p);
  return out;
}

// Build a valid single-entry ZIP archive containing `content` encrypted with
// legacy ZipCrypto under `password`. The entry is "stored" (no deflate).
// Reimplemented in terms of the multi-entry writer for a single code path.
export async function buildEncryptedZip(
  filename: string,
  content: Uint8Array,
  password: string,
): Promise<Uint8Array> {
  return buildEncryptedZipMulti([{ name: filename, bytes: content }], password);
}
